import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import cn from 'classnames';
import { isAfter, isBefore } from 'date-fns';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import _pick from 'lodash/pick';
import schedulingWizardApi, {
  SchedulingWizard,
  SchedulingWizardNewVote,
} from '../../api/schedulingWizard';
import { getSettings } from '../../api/tenants';
import { SnackMessage, Text } from '../../components/common';
import SchedulingWizardVoteForm, {
  FormInitialValues,
  ParsedFormValues as FormValues,
} from '../../components/forms/scheduling-wizard-vote-form';
import { COLORS } from '../../theme/variables';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: '40px 16px',
    minHeight: '100%',
    boxSizing: 'border-box',

    [theme.breakpoints.up('xs')]: {
      width: 'calc(100% - 60px)',
    },

    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
  block: {
    width: '700px',
    padding: '32px',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 2px rgba(34, 91, 187, 0.16), 0px 4px 8px rgba(51, 126, 255, 0.04), 0px 8px 16px rgba(51, 126, 255, 0.04)',
    borderRadius: 0,
    boxSizing: 'border-box',
    alignSelf: 'flex-start',

    [theme.breakpoints.up('xs')]: {
      borderRadius: '16px',
    },
  },
  loadingContainer: {
    height: '100vh',
    padding: 0,
    alignItems: 'center',

    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
  },
  successBlock: {
    width: 300,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    borderRadius: '16px',
  },
  successIcon: {
    fontSize: 30,
    color: COLORS.COLOR_GREEN_BASE,
    marginBottom: 15,
  },
  errorIcon: {
    fontSize: 30,
    color: COLORS.COLOR_RED_BASE,
    marginBottom: 15,
  },
  successText: {
    maxWidth: '100%',
    textAlign: 'center',
    wordWrap: 'break-word',
  },
  title: {
    marginBottom: 15,
  },
  description: {
    marginBottom: 0,
  },
}));

function parseFilterFromURL(search: string): any {
  return _pick(parseQuery(search), ['key']);
}

const formatValuesRequest = (
  values: FormValues,
  fullDetails: SchedulingWizard,
  key: string,
): SchedulingWizardNewVote => {
  return {
    id: fullDetails.id,
    tenantId: fullDetails.tenantId,
    ventureId: fullDetails.ventureId,
    selectedTimeSlotIds: values.selectedTimeSlotIds,
    key,
    originatorEmail: values.email,
  };
};

const getInitialValues = (fullDetails: SchedulingWizard): FormInitialValues => {
  return {
    venture: fullDetails.ventureName || '',
  };
};

interface Payload extends SchedulingWizard {
  email: string;
}

function ReportPage() {
  const classes = useStyles();
  const location = useLocation();

  const [fullDetails, setFullDetails] = useState<Payload>();
  const [statusForm, setStatusForm] = useState<
    'success' | 'loading' | 'error' | ''
  >('loading');
  const [isLoadingSaveForm, setIsLoadingSaveForm] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState('');

  const { key } = useMemo(
    () => parseFilterFromURL(location.search),
    [location.search],
  );

  const initialValues = useMemo(() => {
    if (fullDetails) {
      return getInitialValues(fullDetails);
    }
    return undefined;
  }, [fullDetails]);

  const handleSubmit = async (values: FormValues) => {
    const valuesRequest = formatValuesRequest(
      values,
      fullDetails as SchedulingWizard,
      key,
    );
    try {
      setIsLoadingSaveForm(true);
      await schedulingWizardApi.schedulingWizardVotes(valuesRequest);
      setStatusForm('success');
      setIsLoadingSaveForm(false);
    } catch (e: any) {
      setIsLoadingSaveForm(false);
      const messageError = e.response?.data?.message || 'Internal server error';
      enqueueSnackbar('An error occurred while saving your report', {
        variant: 'error',
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  const loadDetails = async (key: string) => {
    try {
      setStatusForm('loading');
      const responseDetails = await schedulingWizardApi.getSchedulingWizard(
        key,
      );
      const { email } = await getSettings(responseDetails.tenantId);
      responseDetails.schedulingWizardDates.sort((_dateA, _dateB) => {
        const dateA = new Date(_dateA.startDateTime);
        const dateB = new Date(_dateB.startDateTime);

        if (isAfter(dateA, dateB)) {
          return 1;
        }
        if (isBefore(dateA, dateB)) {
          return -1;
        }
        return 0;
      });
      setFullDetails({ ...responseDetails, email });
      setStatusForm('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Internal server error');
      setStatusForm('error');
    }
  };

  useEffect(() => {
    loadDetails(key);
  }, [key]);

  useEffect(() => {
    document.body.style.minWidth = 'auto';
  }, []);

  if (statusForm === 'loading') {
    return (
      <div
        data-testid='report-page'
        className={cn(classes.container, classes.loadingContainer)}
      >
        <CircularProgress size={54} color='primary' />
      </div>
    );
  }

  if (statusForm === 'success') {
    return (
      <div
        data-testid='report-page-success'
        className={cn(classes.container, classes.loadingContainer)}
      >
        <div className={cn(classes.block, classes.successBlock)}>
          <CheckCircleIcon className={classes.successIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>
              Thank you! Your form has been submitted successfully!
            </Text>
          </div>
        </div>
      </div>
    );
  }

  if (statusForm === 'error' || !fullDetails) {
    return (
      <div
        data-testid='report-page-error'
        className={cn(classes.container, classes.loadingContainer)}
      >
        <div className={cn(classes.block, classes.successBlock)}>
          <CancelIcon className={classes.errorIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>
              Unfortunately, an error occurred while submitting your form.
              Please try again in a few minutes. If the problem persists, please
              share the URL with the support team at support@tractionfive.com.
              <br />
              Details: {error}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid='report-page' className={classes.container}>
      <div className={classes.block}>
        <Typography variant='h2' className={classes.title}>
          Scheduling Wizard
        </Typography>
        <div className={classes.description}>
          <Text variant='normal'>
            Your founders requested a new Session with you. Please select
            time slots below that work for you. As soon as your team votes,
            we will schedule a new session automatically.
          </Text>
        </div>
        <SchedulingWizardVoteForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          loading={isLoadingSaveForm}
          schedulingWizardDates={fullDetails.schedulingWizardDates}
          location={fullDetails.physicalLocation ? 'in-person' : 'virtual'}
          adminEmail={fullDetails.email}
          votesNumber={fullDetails.votesNumber}
        />
      </div>
    </div>
  );
}

export default ReportPage;
